/**
 * Textarea UI primitive with label and error display.
 *
 * Mirrors the Input component API (ADR-022 Pattern 5: forwardRef) but renders
 * a <textarea> element. Use this for any multi-line text field with a label.
 *
 * For fields that need custom label layouts (e.g. AI-assisted fields with
 * AiAssistButton next to the label), use a raw <textarea> with manual wrapper
 * instead — same pattern as raw <input> in those contexts.
 */
import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Visible label text rendered above the textarea. */
  label?: string;
  /** Error message rendered below the textarea. */
  error?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ label, error, className, id: externalId, ...props }, ref) {
    const generatedId = useId();
    const textareaId = externalId ?? generatedId;
    const errorId = error ? `${textareaId}-error` : undefined;

    return (
      <div className="input-wrapper">
        {label && (
          <label className="input-label" htmlFor={textareaId}>
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={`input input--textarea ${error ? 'input--error' : ''} ${className ?? ''}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
          {...props}
        />
        {error && (
          <span id={errorId} className="input-error" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  },
);
